import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { createClerkSupabaseClient } from '../../../../lib/supabase.js';
import { formatDateTime, formatNumber, formatUF, formatDate } from '../../../../styles/tokens/formatters';
import DaysSegmentedControl, { DAY_RANGE_OPTIONS } from '../components/DaysSegmentedControl.jsx';
import TrendChart from '../components/TrendChart.jsx';

// Definición de configuraciones por etapa
// Se ajustaron los 'listSelect' a las columnas REALES de las vistas según vistas_company.json
// Se eliminaron las referencias a 'has_*' ya que no existen en las vistas. 
// La info de 'has_*' se calculará via 'enrichRows'.

const STAGES = {
  vistas: {
    title: 'Vistas',
    viewName: 'sesiones',
    listSelect: 'id,created_at,canal,user_id,ruta_inicio,historial_navegacion,datos_simulacion'
  },
  sesiones: {
    title: 'Sesiones',
    viewName: 'sesiones_funnel',
    listSelect: 'id,created_at,canal,ip,user_agent,datos_dps,datos_simulacion'
  },
  simulaciones: {
    title: 'Simulaciones',
    viewName: 'company_cotizaciones',
    listSelect: 'id,created_at,compania_nombre,compania_rut,producto_nombre,plan_nombre,codigo,resumen' 
    // 'resumen' podría contener titular/cargas/prima. 'titular_rut'/'titular_nombre' NO existen en la vista.
  },
  contrataciones: {
    title: 'Contrataciones',
    viewName: 'company_contrataciones',
    listSelect: 'id,created_at,compania_nombre,compania_rut,producto_nombre,plan_nombre,rut,nombre,estado,codigo,cotizacion_id'
  },
  suscripciones: {
    title: 'Suscripciones',
    viewName: 'company_suscripciones',
    listSelect: 'id,created_at,compania_nombre,compania_rut,plan_name,status,amount,currency,billing_frequency,codigo,contratacion_id,certificado_id'
  },
  certificados: {
    title: 'Certificados',
    viewName: 'company_certificados',
    listSelect: 'id,created_at,compania_nombre,compania_rut,producto_nombre,plan_nombre,tipo_certificado,monto,estado_pago,codigo,cliente_rut,cliente_nombre,email_payload'
  }
};

const SESSION_RANGE_OPTIONS = [
  { key: '7d', label: '7 días', days: 7 },
  { key: '14d', label: '14 días', days: 14 },
  { key: '20d', label: '20 días', days: 20 },
  { key: 'mes', label: 'Mes', days: 30 }
];

const pad2 = (n) => `${n}`.padStart(2, '0');

const toDayKey = (d) => {
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`;
};

const buildRange = (days) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));
  const end = new Date(today);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const buildChartPoints = ({ days, dayCounts }) => {
  const { start } = buildRange(days);
  const points = [];
  for (let i = 0; i < days; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const key = toDayKey(d);
    points.push({
      key,
      label: `${d.getDate()}/${d.getMonth() + 1}`,
      value: dayCounts[key] || 0
    });
  }
  return points;
};

// --- ENRICHMENT LOGIC ---

// Helper para parsear JSON de forma segura
const safeJsonParse = (str) => {
  try {
    return typeof str === 'string' ? JSON.parse(str) : str;
  } catch (e) {
    return null;
  }
};

const hasSimulationPrice = (raw) => {
  const data = safeJsonParse(raw);
  const price = data?.primaCalculada;
  return Boolean(price && (price?.primaUF != null || price?.primaPesos != null || price?.precioBaseId != null));
};

const enrichRows = async ({ stageKey, rows, supabase }) => {
  if (!rows || rows.length === 0) return rows;
  const enriched = [...rows];
  const ids = rows.map(r => r.id);

  try {
    if (stageKey === 'vistas') {
      const { data: cots } = await supabase
        .from('company_cotizaciones')
        .select('id, sesion_id')
        .in('sesion_id', ids);

      const sesionToCotIds = {};
      const cotIds = [];
      cots?.forEach(c => {
        if (!c?.sesion_id || !c?.id) return;
        if (!sesionToCotIds[c.sesion_id]) sesionToCotIds[c.sesion_id] = [];
        sesionToCotIds[c.sesion_id].push(c.id);
        cotIds.push(c.id);
      });

      const uniqueCotIds = Array.from(new Set(cotIds.filter(Boolean)));

      let conts = [];
      if (uniqueCotIds.length > 0) {
        const { data } = await supabase
          .from('company_contrataciones')
          .select('id, cotizacion_id')
          .in('cotizacion_id', uniqueCotIds);
        conts = data || [];
      }

      const cotToContIds = {};
      const contIds = [];
      conts.forEach(c => {
        if (!c?.cotizacion_id || !c?.id) return;
        if (!cotToContIds[c.cotizacion_id]) cotToContIds[c.cotizacion_id] = [];
        cotToContIds[c.cotizacion_id].push(c.id);
        contIds.push(c.id);
      });

      const uniqueContIds = Array.from(new Set(contIds.filter(Boolean)));

      let subs = [];
      if (uniqueContIds.length > 0) {
        const { data } = await supabase
          .from('company_suscripciones')
          .select('id, contratacion_id')
          .in('contratacion_id', uniqueContIds);
        subs = data || [];
      }

      const contToSubIds = {};
      subs.forEach(s => {
        if (!s?.contratacion_id || !s?.id) return;
        if (!contToSubIds[s.contratacion_id]) contToSubIds[s.contratacion_id] = [];
        contToSubIds[s.contratacion_id].push(s.id);
      });

      let certs = [];
      if (uniqueContIds.length > 0) {
        const { data } = await supabase
          .from('company_certificados')
          .select('id, contratacion_id')
          .in('contratacion_id', uniqueContIds);
        certs = data || [];
      }

      const contToCertIds = {};
      certs.forEach(c => {
        if (!c?.contratacion_id || !c?.id) return;
        if (!contToCertIds[c.contratacion_id]) contToCertIds[c.contratacion_id] = [];
        contToCertIds[c.contratacion_id].push(c.id);
      });

      enriched.forEach(row => {
        const cids = sesionToCotIds[row.id] || [];
        const contIdsForSession = [];
        cids.forEach((cid) => {
          (cotToContIds[cid] || []).forEach((contId) => {
            contIdsForSession.push(contId);
          });
        });

        row.has_simulacion = hasSimulationPrice(row?.datos_simulacion);
        row.has_sesion = cids.length > 0;
        row.has_contratacion = contIdsForSession.length > 0;
        row.has_suscripcion = contIdsForSession.some((contId) => (contToSubIds[contId] || []).length > 0);
        row.has_certificado = contIdsForSession.some((contId) => (contToCertIds[contId] || []).length > 0);
      });
    }

    if (stageKey === 'sesiones') {
      // Necesitamos: has_simulacion, has_contratacion, has_suscripcion, has_certificado
      // Relaciones: sesion -> cotizacion -> contratacion -> suscripcion -> certificado
      
      // 1. Buscar cotizaciones vinculadas a estas sesiones
      const { data: cots } = await supabase
        .from('company_cotizaciones')
        .select('id, sesion_id')
        .in('sesion_id', ids);

      const sesionToCotId = {}; 
      const cotIds = [];
      cots?.forEach(c => {
        sesionToCotId[c.sesion_id] = c.id;
        cotIds.push(c.id);
      });

      // 2. Buscar contrataciones vinculadas a estas cotizaciones
      let conts = [];
      if (cotIds.length > 0) {
        const { data } = await supabase
          .from('company_contrataciones')
          .select('id, cotizacion_id')
          .in('cotizacion_id', cotIds);
        conts = data || [];
      }
      
      const cotToContId = {};
      const contIds = [];
      conts.forEach(c => {
        cotToContId[c.cotizacion_id] = c.id;
        contIds.push(c.id);
      });

      // 3. Buscar suscripciones vinculadas a contrataciones
      let subs = [];
      if (contIds.length > 0) {
        const { data } = await supabase
          .from('company_suscripciones')
          .select('id, contratacion_id')
          .in('contratacion_id', contIds);
        subs = data || [];
      }
      const contToSubId = {};
      const subIds = [];
      subs.forEach(s => {
        contToSubId[s.contratacion_id] = s.id;
        subIds.push(s.id);
      });

      // 4. Buscar certificados vinculados a contrataciones OR suscripciones
      // La vista company_certificados tiene contratacion_id y subscription_id
      let certs = [];
      if (contIds.length > 0 || subIds.length > 0) {
          // Construir query compleja o traer por separado. Haremos simple: traer por contratacion_id
          // (ya que cert tiene contratacion_id)
          const { data } = await supabase
            .from('company_certificados')
            .select('id, contratacion_id')
            .in('contratacion_id', contIds); // Si no tiene contratacion directa, podria fallar este check.
          certs = data || [];
      }
      const contToCertId = {};
      certs.forEach(c => {
          if (c.contratacion_id) contToCertId[c.contratacion_id] = c.id;
      });


      // Asignar flags
      enriched.forEach(row => {
        const cotId = sesionToCotId[row.id];
        const contId = cotId ? cotToContId[cotId] : null;
        const subId = contId ? contToSubId[contId] : null;
        const certId = contId ? contToCertId[contId] : null;

        row.has_cotizacion = !!cotId;
        row.has_simulacion = !!cotId;
        row.has_contratacion = !!contId;
        row.has_suscripcion = !!subId;
        row.has_certificado = !!certId;
      });
    }

    if (stageKey === 'simulaciones') {
       // Necesitamos: has_contratacion, has_suscripcion, has_certificado
       // Y extraer datos del resumen json (titular, cargas, prima)

       const cotIds = ids;
       
       // 1. Contrataciones
       const { data: conts } = await supabase
         .from('company_contrataciones')
         .select('id, cotizacion_id')
         .in('cotizacion_id', cotIds);
       
       const cotToContId = {};
       const contIds = [];
       conts?.forEach(c => {
         cotToContId[c.cotizacion_id] = c.id;
         contIds.push(c.id);
       });

       // 2. Suscripciones
       let subs = [];
       if (contIds.length > 0) {
          const { data } = await supabase.from('company_suscripciones').select('id, contratacion_id').in('contratacion_id', contIds);
          subs = data || [];
       }
       const contToSubId = {};
       subs.forEach(s => contToSubId[s.contratacion_id] = s.id);

       // 3. Certificados
       let certs = [];
       if (contIds.length > 0) {
          const { data } = await supabase.from('company_certificados').select('id, contratacion_id').in('contratacion_id', contIds);
          certs = data || [];
       }
       const contToCertId = {};
       certs.forEach(c => contToCertId[c.contratacion_id] = c.id);

       enriched.forEach(row => {
          const contId = cotToContId[row.id];
          row.has_contratacion = !!contId;
          row.has_suscripcion = !!(contId && contToSubId[contId]);
          row.has_certificado = !!(contId && contToCertId[contId]);

          // Parsear resumen
          // Estructura esperada segun company_cotizaciones_rows.json:
          // { "total": { "primaPesos": 20275 }, "numeroCargas": 0, "titular": { ... } }
          const resumen = safeJsonParse(row.resumen);
          
          if (resumen) {
             row.titular_nombre = 'Titular'; // Siempre "Titular" si existe el objeto titular
             row.cargas_count = resumen.numeroCargas || 0;
             // Priorizar UF con verificación robusta
             const ufValue = resumen.total?.primaUF ?? resumen.primaUF;
             const clpValue = resumen.total?.primaPesos ?? resumen.prima;

             if (typeof ufValue !== 'undefined' && ufValue !== null) {
                row.prima_cotizada = ufValue;
                row.currency_type = 'UF';
             } else {
                row.prima_cotizada = clpValue || 0;
                row.currency_type = 'CLP';
             }
          } else {
             row.titular_nombre = 'Titular';
             row.cargas_count = 0;
             row.prima_cotizada = 0;
          }
       });
    }

    if (stageKey === 'contrataciones') {
       // has_suscripcion, has_certificado
       const contIds = ids;

       // 1. Suscripciones
       const { data: subs } = await supabase.from('company_suscripciones').select('id, contratacion_id').in('contratacion_id', contIds);
       const contToSubId = {};
       subs?.forEach(s => contToSubId[s.contratacion_id] = s.id);

       // 2. Certificados
       const { data: certs } = await supabase.from('company_certificados').select('id, contratacion_id').in('contratacion_id', contIds);
       const contToCertId = {};
       certs?.forEach(c => contToCertId[c.contratacion_id] = c.id);

       enriched.forEach(row => {
          row.has_suscripcion = !!contToSubId[row.id];
          row.has_certificado = !!contToCertId[row.id];
       });
    }

    if (stageKey === 'suscripciones') {
       // has_certificado. Y nombre del cliente desde contrataciones si no viene
       // Ademas: Suma de subscription_charges y estado de pago (al día/rechazado)
       const subIds = ids;
       // company_suscripciones tiene contratacion_id
       const contIds = rows.map(r => r.contratacion_id).filter(Boolean);
       
       // 1. Certificados (buscamos por subscription_id o contratacion_id)
       // View company_certificados tiene subscription_id
       const { data: certs } = await supabase.from('company_certificados').select('id, subscription_id').in('subscription_id', subIds);
       const subToCertId = {};
       certs?.forEach(c => subToCertId[c.subscription_id] = c.id);

       // 2. Nombre cliente (desde contrataciones)
       let conts = [];
       if (contIds.length > 0) {
          const { data } = await supabase.from('company_contrataciones').select('id, nombre').in('id', contIds);
          conts = data || [];
       }
       const contToName = {};
       conts.forEach(c => contToName[c.id] = c.nombre);

       // 3. Subscription Charges (Total cobrado y estado)
       // Buscar charges asociados a estas suscripciones
       const { data: charges } = await supabase
         .from('company_subscription_charges')
         .select('subscription_id, amount, status')
         .in('subscription_id', subIds);
       
       const subCharges = {}; // { subId: { total: 0, hasFailed: false } }
       charges?.forEach(c => {
          if (!subCharges[c.subscription_id]) {
             subCharges[c.subscription_id] = { total: 0, hasFailed: false, count: 0 };
          }
          if (c.status === 'success' || c.status === 'paid') {
             subCharges[c.subscription_id].total += (c.amount || 0);
          }
          if (c.status === 'failed' || c.status === 'rejected') {
             subCharges[c.subscription_id].hasFailed = true;
          }
          subCharges[c.subscription_id].count++;
       });

       enriched.forEach(row => {
          row.has_certificado = !!subToCertId[row.id] || (row.certificado_id != null);
          if (!row.cliente_nombre && row.contratacion_id) {
             row.cliente_nombre = contToName[row.contratacion_id];
          }
          
          const chargeInfo = subCharges[row.id] || { total: 0, hasFailed: false, count: 0 };
          row.total_charged = chargeInfo.total;
          
          // Logica estado pago: 
          // Si tiene fallidos -> Rechazado (rojo)
          // Si no tiene fallidos y count > 0 -> Al día (verde)
          // Si count == 0 -> Pendiente/Nuevo (gris)
          if (chargeInfo.hasFailed) row.payment_status_dot = 'red';
          else if (chargeInfo.count > 0) row.payment_status_dot = 'green';
          else row.payment_status_dot = 'gray';
       });
    }
    
    if (stageKey === 'certificados') {
       // Titular y Cargas. View tiene cliente_nombre.
       // Prioridad 1: Extraer snapshot de email_payload (datosCompletos)
       enriched.forEach(row => {
          const payload = safeJsonParse(row.email_payload);
          if (payload?.datosCompletos) {
             const datos = payload.datosCompletos;
             const resumen = datos.cotizaciones?.resumen || datos.cotizacion?.resumen;
             if (resumen && typeof resumen.numeroCargas !== 'undefined') {
                row.cargas_count = resumen.numeroCargas;
             }
             if (!row.titular_nombre) {
                row.titular_nombre = datos.titular?.nombre;
             }
             
             // Extraer fechas de cobertura si no vienen en la vista
             if (!row.fecha_inicio_cobertura && datos.fecha_inicio) {
                row.fecha_inicio_cobertura = datos.fecha_inicio;
             }
             if (!row.fecha_termino_cobertura && datos.fecha_fin) {
                row.fecha_termino_cobertura = datos.fecha_fin;
             }
          }
       });

       // Prioridad 2: Ir a cotizaciones (live data) si falta info
       const cotIds = rows
         .filter(r => typeof r.cargas_count === 'undefined' && r.cotizacion_id)
         .map(r => r.cotizacion_id);
       
       if (cotIds.length > 0) {
          const { data: cots } = await supabase.from('company_cotizaciones').select('id, resumen').in('id', cotIds);
          const cotMap = {};
          cots?.forEach(c => cotMap[c.id] = c);
          
          enriched.forEach(row => {
             if (typeof row.cargas_count === 'undefined') {
                 const cot = cotMap[row.cotizacion_id];
                 if (cot) {
                    const resumen = safeJsonParse(cot.resumen);
                    if (resumen) {
                       row.cargas_count = resumen.numeroCargas || 0;
                       if (!row.titular_nombre) row.titular_nombre = resumen.titular?.nombre || resumen.nombre_titular;
                    }
                 }
             }
          });
       }
    }

  } catch (e) {
    console.error("Enrichment error:", e);
  }

  return enriched;
};


// Componente para los Dots de conversión
const ConversionDots = ({ stageKey, row }) => {
  const dots = [];

  if (stageKey === 'vistas') {
    dots.push({ label: 'Sim', active: row.has_simulacion });
  } else if (stageKey === 'sesiones') {
    dots.push({ label: 'Cot', active: row.has_cotizacion });
    dots.push({ label: 'Con', active: row.has_contratacion });
    dots.push({ label: 'Sus', active: row.has_suscripcion });
    dots.push({ label: 'Cer', active: row.has_certificado });
  } else if (stageKey === 'simulaciones') {
    dots.push({ label: 'Con', active: row.has_contratacion });
    dots.push({ label: 'Sus', active: row.has_suscripcion });
    dots.push({ label: 'Cer', active: row.has_certificado });
  } else if (stageKey === 'contrataciones') {
    dots.push({ label: 'Sus', active: row.has_suscripcion });
    dots.push({ label: 'Cer', active: row.has_certificado });
  } else if (stageKey === 'suscripciones') {
    dots.push({ label: 'Cer', active: row.has_certificado });
  } 

  if (dots.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 mt-2">
      {dots.map((dot, idx) => (
        <div 
          key={idx} 
          className={`w-2 h-2 rounded-full ${dot.active ? 'bg-green-400' : 'bg-white/10'}`}
          title={dot.label}
        />
      ))}
      <span className="text-[10px] text-white/40 ml-1">
         Flujo posterior
      </span>
    </div>
  );
};

// Renderizado de contenido principal de la card según etapa
const StageCardContent = ({ stageKey, row }) => {
  
  if (stageKey === 'vistas') {
    const history = safeJsonParse(row?.historial_navegacion);
    const routes = Array.isArray(history) ? history.map((item) => item?.ruta).filter(Boolean) : [];
    const mainRoute = row?.ruta_inicio || routes[0];

    return (
      <div>
        <div className="flex justify-between items-start">
           <div className="min-w-0 pr-2">
              <div className="text-sm font-semibold text-white">
                {row.canal ? `Canal: ${row.canal}` : 'Canal desconocido'}
              </div>
              <div className="text-xs text-white/60 mt-0.5">
                {formatDateTime(row.created_at)}
              </div>
              {mainRoute ? (
                <div className="text-[10px] text-white/50 mt-1 truncate">Ruta: {mainRoute}</div>
              ) : null}
              {row.user_id ? (
                <div className="text-[10px] text-white/40 mt-1 font-mono truncate">user_id: {row.user_id}</div>
              ) : null}
           </div>
        </div>
        <ConversionDots stageKey={stageKey} row={row} />
      </div>
    );
  }

  if (stageKey === 'sesiones') {
    return (
      <div>
        <div className="flex justify-between items-start">
           <div>
              <div className="text-sm font-semibold text-white">
                {row.canal ? `Canal: ${row.canal}` : 'Canal desconocido'}
              </div>
              <div className="text-xs text-white/60 mt-0.5">
                {formatDateTime(row.created_at)}
              </div>
           </div>
        </div>
        <ConversionDots stageKey={stageKey} row={row} />
      </div>
    );
  }

  if (stageKey === 'simulaciones') {
    return (
      <div>
        <div className="flex justify-between items-start">
           <div>
              <div className="text-sm font-semibold text-white">
                 {row.producto_nombre}
              </div>
              <div className="text-xs text-white/80 mt-0.5">
                 Plan: {row.plan_nombre}
              </div>
           </div>
           <div className="text-xs text-white/50 font-mono">
             #{row.codigo || row.id.slice(0,8)}
           </div>
        </div>
        
        <div className="mt-2 text-xs text-white/70 space-y-0.5">
           <div>Titular {row.cargas_count > 0 ? `+ ${row.cargas_count} carga${row.cargas_count > 1 ? 's' : ''}` : ''}</div>
           <div>Prima: {row.prima_cotizada ? (row.currency_type === 'UF' ? formatUF(row.prima_cotizada) : formatNumber(row.prima_cotizada)) : '-'}</div>
        </div>

        <ConversionDots stageKey={stageKey} row={row} />
      </div>
    );
  }

  if (stageKey === 'contrataciones') {
    return (
      <div>
        <div className="flex justify-between items-start">
           <div className="min-w-0 pr-2">
              <div className="text-sm font-semibold text-white truncate">
                 {row.nombre || 'Sin nombre'}
              </div>
              <div className="text-xs text-white/60 mt-0.5">
                 {row.producto_nombre} • {row.plan_nombre}
              </div>
           </div>
           <div className="text-right shrink-0">
             <div className="text-xs text-white/50 font-mono mb-0.5">
               {row.codigo}
             </div>
             <div className="text-[10px] text-white/40">
               #{row.id.slice(0,8)}
             </div>
           </div>
        </div>
        <ConversionDots stageKey={stageKey} row={row} />
      </div>
    );
  }

  if (stageKey === 'suscripciones') {
    // Definir color del dot de estado pago
    let dotColor = 'bg-gray-500';
    if (row.payment_status_dot === 'green') dotColor = 'bg-green-400';
    if (row.payment_status_dot === 'red') dotColor = 'bg-red-400';

    return (
      <div>
        <div className="flex justify-between items-start">
           <div className="min-w-0 pr-2">
              <div className="text-sm font-semibold text-white truncate">
                 {row.cliente_nombre || 'Cliente desconocido'}
              </div>
              <div className="text-xs text-white/50 font-mono mt-0.5">
                 {row.codigo}
              </div>
           </div>
           <div className="text-right shrink-0">
             <div className="text-sm font-bold text-white">
               {row.amount ? formatNumber(row.amount) : '$0'}
             </div>
             <div className="text-[10px] text-white/40 mt-0.5">
               {row.currency || 'CLP'}
             </div>
           </div>
        </div>
        
        {/* Info adicional: Total cobrado y Estado Pago */}
        <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between text-xs text-white/70">
           <div>
              Cobrado: <span className="text-white">{formatNumber(row.total_charged || 0)}</span>
           </div>
           <div className="flex items-center gap-1.5">
              <span>Estado pago</span>
              <div className={`w-2 h-2 rounded-full ${dotColor}`} />
           </div>
        </div>

        <ConversionDots stageKey={stageKey} row={row} />
      </div>
    );
  }

  if (stageKey === 'certificados') {
    return (
      <div>
        <div className="flex justify-between items-start">
           <div className="min-w-0 pr-2">
              <div className="text-sm font-semibold text-white truncate">
                 {row.titular_nombre || row.cliente_nombre || 'Titular desconocido'}
              </div>
              <div className="text-xs text-white/60 mt-0.5">
                 {row.compania_nombre}
              </div>
           </div>
           <div className="text-right shrink-0">
             <div className="text-xs text-white/50 font-mono">
               {row.codigo}
             </div>
           </div>
        </div>
        
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/70 border-t border-white/5 pt-2">
           <div>Cargas: <span className="text-white">{row.cargas_count || 0}</span></div>
           <div>Monto: <span className="text-white">{row.monto ? formatNumber(row.monto) : '-'}</span></div>
        </div>

        {/* Fechas de Cobertura */}
        <div className="mt-2 text-[10px] text-white/50 flex items-center gap-2">
           {row.fecha_inicio_cobertura && (
             <div>Inicio: {formatDate(row.fecha_inicio_cobertura)}</div>
           )}
           {row.fecha_termino_cobertura && (
             <div>Fin: {formatDate(row.fecha_termino_cobertura)}</div>
           )}
        </div>
      </div>
    );
  }

  return (
    <div className="text-sm text-white/50">
       Vista no configurada para {stageKey}
    </div>
  );
};


const fetchCreatedAtPaged = async ({ supabase, viewName, startIso, endIso, pageSize, maxPages, withSimulacion }) => {
  const all = [];
  let from = 0;
  for (let page = 0; page < maxPages; page += 1) {
    let query = supabase
      .from(viewName)
      .select('created_at')
      .gte('created_at', startIso)
      .lte('created_at', endIso)
      .order('created_at', { ascending: true });

    if (withSimulacion) {
      query = query.not('datos_simulacion', 'is', null).neq('datos_simulacion', '{}');
    }

    const res = await query.range(from, from + pageSize - 1);
    if (res.error) throw res.error;
    const data = Array.isArray(res.data) ? res.data : [];
    all.push(...data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
};

const ConversionesMobileStage = () => {
  const { stageKey } = useParams();
  const config = stageKey ? STAGES[stageKey] : null;

  const { session } = useSession();
  const supabase = useMemo(() => createClerkSupabaseClient(session), [session]);

  const [rangeKey, setRangeKey] = useState('7d');
  const [rangeCount, setRangeCount] = useState(0);
  const [chartPoints, setChartPoints] = useState([]);
  const [prevChartPoints, setPrevChartPoints] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const rangeOptions = useMemo(
    () => (stageKey === 'sesiones' ? SESSION_RANGE_OPTIONS : DAY_RANGE_OPTIONS),
    [stageKey]
  );

  const selectedRange = useMemo(
    () => rangeOptions.find((option) => option.key === rangeKey) || rangeOptions[0],
    [rangeKey, rangeOptions]
  );
  const rangeDays = selectedRange?.days || 7;
  const rangeLabel = selectedRange?.key === 'mes'
    ? 'Último mes'
    : selectedRange?.key === 'anio'
      ? 'Último año'
      : `Últimos ${selectedRange?.label || '7 días'}`;

  // Ajuste para Certificados: Asegurar que pedimos cotizacion_id
  if (stageKey === 'certificados' && config && !config.listSelect.includes('cotizacion_id')) {
     config.listSelect += ',cotizacion_id';
  }

  useEffect(() => {
    if (!session) return;
    if (!config) return;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const { start, end } = buildRange(rangeDays);
        const startIso = start.toISOString();
        const endIso = end.toISOString();

        const prevStart = new Date(start);
        prevStart.setDate(start.getDate() - rangeDays);
        const prevStartIso = prevStart.toISOString();
        const prevEnd = new Date(start);
        prevEnd.setMilliseconds(-1);
        const prevEndIso = prevEnd.toISOString();

        const requiresSimulation = stageKey === 'sesiones';
        let countQuery = supabase
          .from(config.viewName)
          .select('id', { count: 'exact', head: true })
          .gte('created_at', startIso)
          .lte('created_at', endIso);
        let listQuery = supabase
          .from(config.viewName)
          .select(config.listSelect)
          .gte('created_at', startIso)
          .lte('created_at', endIso)
          .order('created_at', { ascending: false })
          .limit(30);

        if (requiresSimulation) {
          countQuery = countQuery.not('datos_simulacion', 'is', null).neq('datos_simulacion', '{}');
          listQuery = listQuery.not('datos_simulacion', 'is', null).neq('datos_simulacion', '{}');
        }

        const [countRes, listRes, createdAtRows, prevCreatedAtRows] = await Promise.all([
          countQuery,
          listQuery,
          fetchCreatedAtPaged({ supabase, viewName: config.viewName, startIso, endIso, pageSize: 1000, maxPages: 10, withSimulacion: requiresSimulation }),
          fetchCreatedAtPaged({ supabase, viewName: config.viewName, startIso: prevStartIso, endIso: prevEndIso, pageSize: 1000, maxPages: 10, withSimulacion: requiresSimulation })
        ]);

        if (countRes.error) throw countRes.error;
        if (listRes.error) throw listRes.error;

        setRangeCount(countRes.count || 0);
        let initialRows = Array.isArray(listRes.data) ? listRes.data : [];
        
        // Enrich rows
        const enrichedRows = await enrichRows({ stageKey, rows: initialRows, supabase });
        setRows(enrichedRows);

        const dayCounts = {};
        for (const r of createdAtRows) {
          const raw = r?.created_at;
          if (!raw) continue;
          const d = new Date(raw);
          if (Number.isNaN(d.getTime())) continue;
          const key = toDayKey(d);
          dayCounts[key] = (dayCounts[key] || 0) + 1;
        }

        setChartPoints(buildChartPoints({ days: rangeDays, dayCounts }));
      } catch (e) {
        console.error("Error loading stage data:", e);
        setError(e?.message || 'Error cargando etapa.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [session, supabase, config, rangeDays, stageKey]);

  if (!config) {
    return (
      <div className="min-h-screen bg-[#0B0B0F] text-white">
        <div className="mx-auto w-full max-w-[520px] px-4 pt-6 pb-8">
          <Link to="/conversiones/m" className="text-xs text-white/80">
            Volver
          </Link>
          <div className="mt-6 text-sm text-red-300">Etapa no encontrada</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white">
      <div className="mx-auto w-full max-w-[520px] px-4 pt-6 pb-8">
        <div className="flex items-center justify-between gap-3">
          <Link to="/conversiones/m" className="text-xs text-white/80 flex items-center gap-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            Volver
          </Link>
          <DaysSegmentedControl value={rangeKey} onChange={setRangeKey} options={rangeOptions} />
        </div>

        <div className="mt-4">
          <div className="text-lg font-semibold">{config.title}</div>
          <div className="text-xs text-white/70 mt-1">{rangeLabel}</div>
        </div>

        <div className="mt-4 rounded-[18px] border border-white/10 bg-white/5 p-4">
          <div className="text-xs text-white/70">Total en rango</div>
          <div className="mt-1 text-2xl font-bold">{formatNumber(rangeCount)}</div>
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="text-sm text-white/70">Cargando...</div>
          ) : error ? (
            <div className="text-sm text-red-300">{error}</div>
          ) : (
            <TrendChart points={chartPoints} prevPoints={prevChartPoints} />
          )}
        </div>

        <div className="mt-6">
          <div className="text-sm font-semibold mb-3">Detalles recientes</div>
          {loading ? (
            <div className="mt-3 text-sm text-white/70">Cargando...</div>
          ) : error ? (
            <div className="mt-3 text-sm text-red-300">{error}</div>
          ) : rows.length === 0 ? (
            <div className="mt-3 text-sm text-white/70">Sin registros</div>
          ) : (
            <div className="space-y-2">
              {rows.map((row) => (
                <div
                  key={row?.id || row?.codigo || JSON.stringify(row)}
                  className="rounded-[18px] border border-white/10 bg-white/5 p-4"
                >
                  <StageCardContent stageKey={stageKey} row={row} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversionesMobileStage;
